import Swiper from 'swiper';
import {
    A11y,
    Navigation
} from 'swiper/modules';
import 'swiper/css';

/**
 * Master layout: Image Carousel (Large / Small variants).
 * Large: ~1 full slide + peek; capped at 920px on lg+.
 * Small desktop: ~2 slides + peek; capped at 400px / 540px.
 *
 * When the viewport is wider than one slide, Swiper drops snap points unless
 * slidesOffsetAfter is set — that breaks left-anchored snapping and prevents
 * the last slide from reaching the lead position.
 */
function getMetrics(el, isLarge) {
    const containerWidth = el.clientWidth;
    const isMd = window.matchMedia('(min-width: 768px)').matches;
    const isLg = window.matchMedia('(min-width: 1024px)').matches;

    if (isLarge) {
        const visible = isMd ? 1.18 : 1.1;
        const gap = isMd ? 24 : 16;
        let slideWidth = (containerWidth - gap * (visible - 1)) / visible;
        if (isLg) {
            slideWidth = Math.min(slideWidth, 920);
        }
        slideWidth = Math.floor(slideWidth);
        return {
            slideWidth,
            gap,
            offsetAfter: Math.max(0, containerWidth - slideWidth),
        };
    }

    const visible = isMd ? 2.22 : 1.08;
    const gap = isMd ? 16 : 12;
    let slideWidth = (containerWidth - gap * (visible - 1)) / visible;
    if (isMd && isLg) {
        slideWidth = Math.min(slideWidth, 540);
    } else if (isMd) {
        slideWidth = Math.min(slideWidth, 400);
    }
    slideWidth = Math.floor(slideWidth);
    return {
        slideWidth,
        gap,
        offsetAfter: Math.max(0, containerWidth - slideWidth),
    };
}

function setSlideWidths(el, slideWidth) {
    el.style.setProperty('--image-carousel-slide-width', `${slideWidth}px`);
    el.querySelectorAll('.swiper-slide').forEach((slide) => {
        slide.style.width = `${slideWidth}px`;
    });
}

function applyMetrics(el, swiper, isLarge) {
    const { slideWidth, gap, offsetAfter } = getMetrics(el, isLarge);

    setSlideWidths(el, slideWidth);
    swiper.params.spaceBetween = gap;
    swiper.params.slidesOffsetAfter = offsetAfter;

    const activeIndex = swiper.activeIndex;
    swiper.update();
    swiper.slideTo(activeIndex, 0, false);
}

export function initImageCarousels() {
    document.querySelectorAll('[data-image-carousel]').forEach((sectionEl) => {
        const el = sectionEl.querySelector('.image-carousel__swiper');
        if (!el) {
            return;
        }

        const variant = sectionEl.getAttribute('data-image-carousel-variant') || 'large';
        const slideCount = el.querySelectorAll('.swiper-slide').length;
        if (slideCount < 1) {
            return;
        }

        const prevEl = sectionEl.querySelector('.image-carousel__nav-prev');
        const nextEl = sectionEl.querySelector('.image-carousel__nav-next');
        const isLarge = variant === 'large';
        const initial = getMetrics(el, isLarge);

        setSlideWidths(el, initial.slideWidth);

        let resizeTimer;
        const onWindowResize = () => {
            clearTimeout(resizeTimer);
            resizeTimer = window.setTimeout(() => {
                if (el.swiper) {
                    applyMetrics(el, el.swiper, isLarge);
                }
            }, 100);
        };

        new Swiper(el, {
            modules: [Navigation, A11y],
            slidesPerView: 'auto',
            slidesPerGroup: 1,
            spaceBetween: initial.gap,
            slidesOffsetAfter: initial.offsetAfter,
            speed: 450,
            grabCursor: true,
            loop: false,
            rewind: slideCount > 1,
            watchOverflow: true,
            roundLengths: true,
            centeredSlides: false,
            observer: true,
            observeParents: true,
            a11y: {
                enabled: true,
                prevSlideMessage: 'Previous slide',
                nextSlideMessage: 'Next slide',
            },
            navigation: {
                prevEl: prevEl || undefined,
                nextEl: nextEl || undefined,
            },
            on: {
                init(swiper) {
                    applyMetrics(el, swiper, isLarge);
                    window.addEventListener('resize', onWindowResize);
                },
                beforeDestroy() {
                    window.removeEventListener('resize', onWindowResize);
                    clearTimeout(resizeTimer);
                },
            },
        });
    });
}
